import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { getHRData, saveHRForm } from '../../../../../../api/transitions';
import { enqueueSnackbar } from '../../../../../../reusable-components/snackbar';
import SkeletonBlock from '../../../SkeletonBlock';
import { HR } from '../../../formQuestionsEnum';
import FacilityUnionForm from './FacilityUnionForm';
import FMLAForm from './FMLAForm';
import DepartmentsContractedForm from './DepartmentsContractedForm';
import { flattenTransitionSubmitData } from '../../../formDataUtils';
import { trimData } from '../../../../../../utils/formatText';

const HRView = forwardRef(({ setFormData }, ref) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const facilityId = searchParams.get('facilityId');
  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const userPermissions = useSelector((state) => state.transitions?.data?.userPermissions);

  const HRPermissions = Object.fromEntries(
    Object.values(userPermissions?.[id]?.[facilityId]?.hrFieldPermissions || {}).map((x) => [
      x.formQuestionId,
      x.isEditable,
    ])
  );

  useEffect(() => {
    getHRData(id, facilityId)
      .then((resp) => {
        setData(resp.data);
      })
      .catch(() => {
        enqueueSnackbar('Failed to load HR data', { variant: 'error' });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id, facilityId]);

  useEffect(() => {
    const fieldNameMap = Object.fromEntries(Object.entries(HR).map(([, v]) => [v.id, v.fieldName]));

    const mappedFields = {
      ...data?.fields?.reduce((acc, field) => ({ ...acc, [fieldNameMap[field.formQuestionId]]: field.answer }), {}),
    };

    setFormData({ transitionFacilityId: data?.transitionFacilityId, ...mappedFields });
  }, [data]);

  const submitForm = async (allData) => {
    const flattenData = flattenTransitionSubmitData(allData);
    const result = trimData(flattenData);

    await saveHRForm({
      id,
      sellerFacilityId: facilityId,
      ...result,
    })
      .then((resp) => {
        enqueueSnackbar('HR data saved successfully', { variant: 'success' });
        setData(resp.data);
      })
      .catch(() => {
        enqueueSnackbar('Failed to save HR data', { variant: 'error' });
      });
  };

  useImperativeHandle(ref, () => ({ submitForm }));

  return isLoading ? (
    <SkeletonBlock height={600} sx={{ mt: 2 }} />
  ) : (
    <>
      <FacilityUnionForm permissions={HRPermissions} />
      <DepartmentsContractedForm permissions={HRPermissions} />
      <FMLAForm permissions={HRPermissions} />
    </>
  );
});

export default HRView;

HRView.propTypes = {
  setFormData: PropTypes.func,
};
